import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';

export const BEQUALITY_ACCESS_STORAGE = 'be.bequality.direct-access.v1';
const errorStorage = `${BEQUALITY_ACCESS_STORAGE}.error`;
const opaque = /^[A-Za-z0-9_-]{43}$/;
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const origins = ['beborne.com', 'becaisse.fr', 'beresto.fr', 'bemenu.fr'].flatMap(host => [`https://${host}`, `https://www.${host}`]);
const brands = ['becaisse', 'bekiosk', 'beresto', 'bemenu'];
const destination = 'https://bequality.fr/app/beresto-open.html';
export const BEQUALITY_ACCESS_ERROR = 'Cet accès de consultation BEQUALITY ne peut pas être ouvert. Recommencez depuis Mes options ou utilisez votre connexion BEQUALITY habituelle.';
const failure = () => new Error(BEQUALITY_ACCESS_ERROR);
const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
function currentAccount() { try { return String(JSON.parse(localStorage.getItem('kioskAccount') || 'null')?.id || ''); } catch (_) { return ''; } }
function assertSession(session, signal) {
  if (signal?.aborted || !session?.authSnapshot || !session?.accountSnapshot || getStoredAuthToken() !== session.authSnapshot || currentAccount() !== session.accountSnapshot) throw failure();
}
async function fingerprint(session, signal) {
  assertSession(session, signal);
  const bytes = new TextEncoder().encode(JSON.stringify([session.authSnapshot, session.accountSnapshot]));
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  assertSession(session, signal);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
function validExpiry(value) { return Number.isFinite(value) && value > Date.now() && value <= Date.now() + 305000; }
function readSaved() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(BEQUALITY_ACCESS_STORAGE) || 'null');
    if (saved?.schemaVersion === 1 && uuid.test(saved.requestId) && opaque.test(saved.browserSecret) && brands.includes(saved.brand)
      && origins.includes(saved.origin) && saved.origin === window.location.origin && saved.accountSnapshot === currentAccount()
      && /^[a-f0-9]{64}$/.test(saved.sessionFingerprint || '') && validExpiry(saved.expiresAt)
      && ['started', 'ready', 'issued'].includes(saved.phase)
      && (saved.phase === 'started' || (exactKeys(saved.callback, ['state', 'codeChallenge']) && opaque.test(saved.callback.state) && opaque.test(saved.callback.codeChallenge)))) return saved;
  } catch (_) { /* No valid tab intent is available. */ }
  return null;
}
function persist(saved) {
  const value = JSON.stringify(saved);
  sessionStorage.setItem(BEQUALITY_ACCESS_STORAGE, value);
  if (sessionStorage.getItem(BEQUALITY_ACCESS_STORAGE) !== value) throw failure();
}
export function validBequalityAccessStart(data, origin = window.location.origin) {
  try {
    const url = new URL(data.authorizationUrl), hash = new URLSearchParams(url.hash.slice(1)), expires = Date.parse(data.expiresAt);
    return exactKeys(data, ['schemaVersion', 'requestId', 'browserSecret', 'authorizationUrl', 'expiresAt'])
      && data.schemaVersion === 1 && uuid.test(data.requestId) && opaque.test(data.browserSecret) && origins.includes(origin)
      && validExpiry(expires) && new Date(expires).toISOString() === data.expiresAt
      && url.origin + url.pathname === destination && !url.search && !url.username && !url.password
      && [...hash.keys()].length === 2 && hash.getAll('requestId').length === 1 && hash.get('requestId') === data.requestId
      && hash.getAll('origin').length === 1 && hash.get('origin') === origin;
  } catch (_) { return false; }
}
export function validBequalityAccessReturn(data, requestId, state) {
  try {
    const url = new URL(data.redirectUrl), hash = new URLSearchParams(url.hash.slice(1));
    return exactKeys(data, ['schemaVersion', 'redirectUrl']) && data.schemaVersion === 1
      && url.origin + url.pathname === destination && !url.search && !url.username && !url.password
      && [...hash.keys()].length === 3 && ['requestId', 'ticket', 'state'].every(key => hash.getAll(key).length === 1)
      && hash.get('requestId') === requestId && hash.get('state') === state && opaque.test(hash.get('ticket') || '');
  } catch (_) { return false; }
}
// Capture before other flows or fetches. Keep the validated callback durable across StrictMode and remounts.
export function captureBequalityAccessCallback() {
  const params = new URLSearchParams(window.location.hash.slice(1));
  if (!params.has('bequality_access_request')) {
    if ([...params.keys()].length) return null;
    const saved = readSaved();
    if (saved?.phase === 'ready') return { access: saved };
    if (saved?.phase === 'issued') return { accessError: BEQUALITY_ACCESS_ERROR, accessBrand: saved.brand };
    try { if (sessionStorage.getItem(errorStorage)) return { accessError: BEQUALITY_ACCESS_ERROR }; } catch (_) { /* No callback. */ }
    return null;
  }
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
  const saved = readSaved();
  const keys = ['bequality_access_request', 'state', 'code_challenge'];
  if (!saved || saved.phase === 'issued' || [...params.keys()].length !== 3 || keys.some(key => params.getAll(key).length !== 1)
    || params.get('bequality_access_request') !== saved.requestId || !opaque.test(params.get('state') || '') || !opaque.test(params.get('code_challenge') || '')) {
    try { sessionStorage.setItem(errorStorage, '1'); sessionStorage.removeItem(BEQUALITY_ACCESS_STORAGE); } catch (_) { /* The failed return remains non-actionable. */ }
    return { accessError: BEQUALITY_ACCESS_ERROR };
  }
  try {
    const next = { ...saved, phase: 'ready', callback: { state: params.get('state'), codeChallenge: params.get('code_challenge') } };
    persist(next); sessionStorage.removeItem(errorStorage); return { access: next };
  } catch (_) { return { accessError: BEQUALITY_ACCESS_ERROR }; }
}
export async function startBequalityAccess(brand, session, signal) {
  assertSession(session, signal);
  if (!brands.includes(brand) || !origins.includes(window.location.origin)) throw failure();
  sessionStorage.removeItem(BEQUALITY_ACCESS_STORAGE); sessionStorage.removeItem(errorStorage);
  const sessionFingerprint = await fingerprint(session, signal);
  const response = await fetch(`${API_URL}/api/admin/integrations/bequality/access/start`, {
    method: 'POST', cache: 'no-store', signal, headers: buildKioskAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ brand }),
  });
  assertSession(session, signal);
  if (!response.ok) throw failure();
  const data = await response.json(); assertSession(session, signal);
  if (!validBequalityAccessStart(data)) throw failure();
  persist({ schemaVersion: 1, requestId: data.requestId, browserSecret: data.browserSecret, expiresAt: Date.parse(data.expiresAt), brand, origin: window.location.origin,
    sessionFingerprint, accountSnapshot: session.accountSnapshot, phase: 'started' });
  assertSession(session, signal);
  return data.authorizationUrl;
}
export async function completeBequalityAccess(access, session, signal) {
  assertSession(session, signal);
  const digest = await fingerprint(session, signal);
  const saved = readSaved();
  if (!saved || saved.phase !== 'ready' || saved.requestId !== access?.requestId || saved.sessionFingerprint !== digest
    || saved.sessionFingerprint !== access.sessionFingerprint || saved.callback.state !== access.callback?.state || saved.callback.codeChallenge !== access.callback?.codeChallenge) throw failure();
  // Persist the claim BEFORE sending. Timeout, abort, remount or double click cannot issue another ticket.
  persist({ ...saved, phase: 'issued' });
  assertSession(session, signal);
  const response = await fetch(`${API_URL}/api/admin/integrations/bequality/access/complete`, {
    method: 'POST', cache: 'no-store', signal, headers: buildKioskAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ requestId: saved.requestId, browserSecret: saved.browserSecret, state: saved.callback.state, codeChallenge: saved.callback.codeChallenge }),
  });
  assertSession(session, signal);
  if (!response.ok) throw failure();
  const data = await response.json(); assertSession(session, signal);
  if (!validBequalityAccessReturn(data, saved.requestId, saved.callback.state)) throw failure();
  sessionStorage.removeItem(BEQUALITY_ACCESS_STORAGE); sessionStorage.removeItem(errorStorage);
  return data.redirectUrl;
}
export function navigateBequalityAccess(url) { window.location.assign(url); }
